import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _typeof from "@babel/runtime/helpers/typeof";
import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { throttle } from './utils';
var isClient = (typeof window === "undefined" ? "undefined" : _typeof(window)) !== undefined && typeof window !== 'undefined';

var useMountedRef = function useMountedRef() {
  var mounted = useRef(false);
  useEffect(function () {
    mounted.current = true;
    return function () {
      mounted.current = false;
    };
  }, []);
  return mounted;
};

var getWindowSize = function getWindowSize(_ref) {
  var initialWidth = _ref.initialWidth,
      initialHeight = _ref.initialHeight;
  if (isClient) return {
    width: window.innerWidth,
    height: window.innerHeight
  };
  return {
    width: initialWidth,
    height: initialHeight
  };
};

var windowSizeSubscribers = new Set();

var dispatchWindowSizeChange = function dispatchWindowSizeChange() {
  windowSizeSubscribers.forEach(function (cb) {
    return cb();
  });
};

var subscribeToWindowSizeChange = function subscribeToWindowSizeChange(callback) {
  var shouldAddListener = !windowSizeSubscribers.size && isClient;
  windowSizeSubscribers.add(callback);

  if (shouldAddListener) {
    window.addEventListener('resize', dispatchWindowSizeChange);
  }

  return function unsubscribeFromWindowSizeChange() {
    windowSizeSubscribers.delete(callback);

    if (!windowSizeSubscribers.size && isClient) {
      window.removeEventListener('resize', dispatchWindowSizeChange);
    }
  };
};

function useWindowSizeOptions() {
  var _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref2$throttleMs = _ref2.throttleMs,
      throttleMs = _ref2$throttleMs === void 0 ? 0 : _ref2$throttleMs,
      _ref2$initialWidth = _ref2.initialWidth,
      initialWidth = _ref2$initialWidth === void 0 ? 0 : _ref2$initialWidth,
      _ref2$initialHeight = _ref2.initialHeight,
      initialHeight = _ref2$initialHeight === void 0 ? 0 : _ref2$initialHeight;

  return useMemo(function () {
    return {
      throttleMs: throttleMs,
      initialWidth: initialWidth,
      initialHeight: initialHeight
    };
  }, [throttleMs, initialWidth, initialHeight]);
}
/**
 * `useWindowSize` is a React hook for components that care about
 * the window size. It can be used statefully or not.
 *
 * @see https://hz-core.netlify.app/use-windowsize
 */


function useWindowSize(handlerOrOptions, windowSizeOptions) {
  var changeHandler = useRef(null);

  if (typeof handlerOrOptions === 'function') {
    changeHandler.current = handlerOrOptions;
  } else {
    windowSizeOptions = handlerOrOptions;
  }

  var options = useWindowSizeOptions(windowSizeOptions);

  var _useState = useState(function () {
    return getWindowSize(options);
  }),
      _useState2 = _slicedToArray(_useState, 2),
      size = _useState2[0],
      setSize = _useState2[1];

  var updateWindowSize = useMemo(function () {
    /**
     * `updateWindowSize` will update the current change handler
     * with a new `WindowSize` whenever the window's size changes.
     */
    function updateWindowSize() {
      var cb = changeHandler.current;
      var size = getWindowSize(options);

      if (typeof cb === 'function') {
        cb(size);
      } else {
        setSize(size);
      }
    }

    return options.throttleMs ? throttle(updateWindowSize, options.throttleMs) : updateWindowSize;
  }, [options]);
  var mounted = useMountedRef();
  var handleSizeChange = useCallback(
  /**
   * `handleSizeChange` ensures component is mounted
   * before calling updateWindowSize()
   */
  function () {
    return mounted.current && updateWindowSize();
  }, [updateWindowSize, mounted]);
  useEffect(function () {
    subscribeToWindowSizeChange(handleSizeChange);
    handleSizeChange();
  }, [handleSizeChange]);

  if (!changeHandler.current) {
    return size;
  }
}

export { useWindowSize };
export default function WindowSizeMonitor(props) {
  var size = useWindowSize();
  return props.children(size);
}